import { merge, uniqBy } from 'lodash';
import { USER_TYPES } from '@medflex/utils/constants';
import isTeamMember from '@medflex/utils/isTeamMember';
import isSelf from '@medflex/utils/isSelf';
import isSupport from '@medflex//utils/isSupportPatient';
import { get as getFullNameWithTitle } from '@medflex/utils/getFullNameWithTitle';
import makeApiRequest, { getCancelToken } from '../../../api/makeApiRequest';

let cancelToken = null;

const normalizeContact = ({ id, attributes }) => {
  const type = [USER_TYPES.STAFF, USER_TYPES.PHYSICIAN, USER_TYPES.PATIENT].find(
    cur => !!attributes[cur],
  );
  const { [type]: user, ...props } = attributes;

  return {
    id,
    ...props,
    pairedAt: props.pairedAt || user.termsAcceptedAt,
    createdAt: props.createdAt || user.createdAt,
    physicianUserId: props.physicianUserId,
    user: {
      ...user,
      initials: `${user.firstName[0]}${user.lastName[0]}`,
      fullNameWithTitle: getFullNameWithTitle(user),
      fullNameWithTitleAndSalutation: getFullNameWithTitle(user, { salutation: true }),
    },
    type,
  };
};

const mapUser = ({ id, attributes, type }) => ({
  user: {
    id,
    ...attributes,
    initials: `${attributes.firstName[0]}${attributes.lastName[0]}`,
    fullNameWithTitle: getFullNameWithTitle(attributes),
    fullNameWithTitleAndSalutation: getFullNameWithTitle(attributes, { salutation: true }),
  },
  type,
  pairedAt: type === USER_TYPES.STAFF && attributes.termsAcceptedAt,
});

const addressBookContacts = {
  name: 'addressBookContacts',
  state: { contacts: [] },
  reducers: {
    onLoaded: (state, payload) => {
      const { data } = payload;
      if (data.length === 0) return state;
      return { ...state, contacts: [...data.map(user => normalizeContact(user))] };
    },
    onAddContact: (state, contact) => ({
      ...state,
      contacts: uniqBy([...state.contacts, normalizeContact(contact)], 'user.id'),
    }),
    onContactsModified: (state, modifiedContacts) => {
      const normalizedContacts = modifiedContacts.map(normalizeContact);

      return {
        ...state,
        contacts: state.contacts.reduce((prev, contact) => {
          const matchedIndex = prev.findIndex(({ user: { id } }) => id === contact.user.id);

          if (matchedIndex !== -1) {
            // eslint-disable-next-line no-param-reassign
            prev[matchedIndex] = { ...contact, ...prev[matchedIndex] };
            return prev;
          }

          prev.push(contact);

          return prev;
        }, normalizedContacts),
      };
    },
    'chatContacts/archive': (state, { contactId }) => {
      const { contacts } = state;
      if (contactId) {
        return {
          ...state,
          contacts: contacts.map(contact =>
            contact.id === contactId
              ? { ...contact, archivedAt: new Date().toISOString() }
              : contact,
          ),
        };
      }
      return state;
    },
    'chatContacts/dearchive': (state, { contactId }) => {
      const { contacts } = state;
      if (contactId) {
        return {
          ...state,
          contacts: contacts.map(contact => {
            if (contact.id === contactId) {
              const { archivedAt, ...rest } = contact;
              return { ...rest };
            }
            return contact;
          }),
        };
      }
      return state;
    },
    'chatContacts/updatePatientData': (state, { contactId, payload }) => {
      const { contacts } = state;
      if (contactId) {
        const { note, pvsId } = payload;
        return {
          ...state,
          contacts: contacts.map(contact =>
            contact.id === contactId
              ? {
                  ...contact,
                  note,
                  pvsId,
                  user: {
                    ...contact.user,
                  },
                }
              : contact,
          ),
        };
      }
      return state;
    },
    'patientInvitations/delete': (state, { contactId }) => {
      const { contacts } = state;
      return {
        ...state,
        contacts: contacts.filter(contact => contact.id !== contactId),
      };
    },
    '_chat/onMessage': (state, event) => {
      const { contacts } = state;
      const data = JSON.parse(event.data);
      if (data.type === 'CONTACT_DELETED') {
        const { payload } = data;

        const deletedIds = payload.map(({ id }) => id);

        return {
          ...state,
          contacts: contacts.filter(
            contact =>
              !deletedIds.includes(contact.id) && !deletedIds.includes(contact.mainContactId),
          ),
        };
      }
      return state;
    },
    'staff/onCreated': (state, payload) => {
      const { contacts } = state;
      // TODO, this information should be like normalizeContact, to ensure the same data structure.
      const { id, userId, ...staff } = payload;
      const normalizedStaff = {
        id: undefined,
        user: {
          id: userId,
          ...staff,
        },
        type: USER_TYPES.STAFF,
      };
      return { ...state, contacts: uniqBy([...contacts, normalizedStaff], 'user.id') };
    },
    onDeleted: (state, id) => {
      const { contacts } = state;
      return {
        ...state,
        contacts: contacts.filter(contact => contact.user.id !== id),
      };
    },
    onUserModified(state, { id, attributes, type }) {
      const { contacts } = state;
      const matchedContact = contacts.find(s => s.user.id === id);
      const mapped = mapUser({ id, attributes, type });
      if (matchedContact) {
        return {
          ...state,
          contacts: [...contacts.map(s => (s.user.id === id ? merge(matchedContact, mapped) : s))],
        };
      }
      return { ...state, contacts: [...contacts, mapped] };
    },
  },
  messageListeners: {
    CONTACT_CREATED({ payload }) {
      this.onAddContact(payload);
    },
    USER_DELETED({ payload }) {
      if (payload.id) this.onDeleted(payload.id);
    },
    USER_MODIFIED({ payload }) {
      const { id, attributes, type } = payload;
      if (id && attributes) {
        this.onUserModified({ id, attributes, type });
      }
    },
    CONTACTS_MODIFIED({ payload }) {
      if (payload.length) {
        this.onContactsModified(payload);
      }
    },
  },
  selectors: (slice, createSelector) => ({
    byType() {
      return createSelector(
        slice,
        ({ superuser }) => superuser,
        ({ contacts }, { id, attributes }) => {
          let self;
          let support;
          const orgaPhysicians = [];
          const pairedPhysicians = [];
          const unpairedPhysicians = [];
          const pairedPatients = [];
          const unpairedPatients = [];
          const allPatients = [];
          const all = [];
          const staff = [];
          const physicianUserId = attributes.physicianUserId || id;

          contacts.forEach(contact => {
            const { user } = contact;
            if (isSelf(user)) {
              self = contact;
              return;
            }

            if (isSupport(user)) {
              support = contact;
              return;
            }

            if (isTeamMember({ contact, physicianUserId })) {
              const staffMember = { isTeam: true, ...contact };
              all.push(staffMember);
              staff.push(staffMember);
            } else {
              all.push(contact);
            }

            if (contact.type === 'physician') {
              if (!isTeamMember({ contact, physicianUserId })) {
                if (!contact.id) {
                  orgaPhysicians.push(contact);
                } else if (!contact.pairedAt) {
                  unpairedPhysicians.push(contact);
                } else {
                  pairedPhysicians.push(contact);
                }
              }
            }

            if (contact.type === 'patient') {
              if (contact.pairedAt) {
                pairedPatients.push(contact);
              } else {
                unpairedPatients.push(contact);
              }
              allPatients.push(contact);
            }
          });

          return {
            self: self ? [self] : [],
            support: support ? [support] : [],
            pairedPhysicians,
            unpairedPhysicians,
            orgaPhysicians,
            staff,
            pairedPatients,
            unpairedPatients,
            allPatients,
            all,
          };
        },
      );
    },
  }),
  effects: () => ({
    async load(_, { oidcSession }) {
      const cToken = getCancelToken();
      if (!cancelToken) {
        cancelToken = cToken;
      } else {
        cancelToken.cancel();
      }

      const requestConfig = { url: '/address-book', cancelToken: cToken.token };
      /** @type any */
      const [request] = makeApiRequest(requestConfig, oidcSession);
      const { error, data } = await request;
      if (!error) this.onLoaded(data);
    },
  }),
};

export default addressBookContacts;
